import React from 'react';
import PageHero from './PageHero';
import { Users, HeartHandshake, GraduationCap, CalendarHeart } from 'lucide-react';
import studentsImg from '../assets/Banner-GMIS Students.png';
import campusImg from '../assets/GMIS Backyard View.png';

export default function AlumniCommunityPage({ setPage }) {
  return (
    <div className="w-full bg-[#f8f9fa] min-h-screen text-ink">
      {/* ── Hero ─────────────────────────────────────────────────────────────── */}
      <PageHero
        title="Our Community"
        eyebrow="School Life at GMIS Jakarta"
        subtitle="Connecting Families, Students and Alumni"
        breadcrumbs={["Home", "School Life", "Alumni Community"]}
        bgImage={studentsImg}
      />

      {/* ── Main Content Container ────────────────────────────────────────── */}
      <div className="max-w-[1200px] mx-auto px-6 lg:px-[80px] py-16 lg:py-24 box-border">

        {/* ── Introduction Lead ── */}
        <div className="max-w-3xl mx-auto text-center mb-20 lg:mb-24">
          <div className="inline-flex items-center gap-2 mb-3 px-3.5 py-1 rounded-full border border-gold/30 bg-gold/10">
            <span className="text-gold text-[11px] font-bold tracking-[0.2em] uppercase">School Life</span>
          </div>
          <h2 className="m-0 font-heading font-bold text-3xl md:text-4xl text-navy tracking-tight mb-4">
            Connecting Families, Students and Alumni
          </h2>
          <div className="w-16 h-1 bg-gold mx-auto mb-6" />
          <p className="text-[17px] md:text-[19px] text-navy font-medium leading-relaxed">
            A school community extends beyond the classroom. At GMIS Jakarta, families and alumni are part of a wider community that stays connected through shared experiences, communication and opportunities to be involved in school life.
          </p>
        </div>

        {/* ── Section 1: Parent Association ── */}
        <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16 mb-20 lg:mb-28">
          <div className="w-full lg:w-5/12">
            <div className="relative">
              <div className="absolute -top-4 -left-4 w-24 h-24 bg-gold/15 rounded-2xl -z-10" />
              <img
                src={studentsImg}
                alt="Parent Association at GMIS"
                className="relative z-10 w-full h-[360px] md:h-[420px] rounded-2xl shadow-xl object-cover border-4 border-white"
              />
              <div className="absolute -bottom-4 -right-4 w-20 h-20 bg-navy/10 rounded-2xl -z-10" />
            </div>
          </div>

          <div className="w-full lg:w-7/12 flex flex-col gap-6 text-left">
            <div>
              <div className="inline-flex items-center gap-2 mb-3 px-3.5 py-1 rounded-full border border-gold/30 bg-gold/10">
                <span className="text-gold text-[11px] font-bold tracking-[0.2em] uppercase">Home & School Partnership</span>
              </div>
              <h2 className="m-0 font-heading font-bold text-3xl md:text-4xl text-navy tracking-tight">
                Parent Association
              </h2>
              <div className="w-16 h-1 bg-gold mt-4" />
            </div>

            <p className="text-[16px] md:text-[17px] font-medium text-navy leading-relaxed">
              A strong partnership between home and school plays an important role in supporting every child’s educational journey. Parents are encouraged to stay involved in their child’s learning, communicate with teachers and coordinators, and take part in school activities and programmes.
            </p>

            <div className="p-6 rounded-2xl bg-white border border-line shadow-sm">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-gold/15 text-gold-ink flex items-center justify-center shrink-0 mt-0.5">
                  <Users className="w-5 h-5 text-gold-ink" />
                </div>
                <p className="text-[16px] text-ink/80 leading-relaxed m-0">
                  The Parent-Teachers Association provides an opportunity for parents to come together as part of the school community. Parent-Teacher Meets, workshops, orientation sessions, open houses and Coffee Mornings create opportunities for families to connect and stay engaged with school life.
                </p>
              </div>
            </div>

            <div className="p-6 rounded-2xl bg-white border border-line shadow-sm">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-navy/10 text-navy flex items-center justify-center shrink-0 mt-0.5">
                  <CalendarHeart className="w-5 h-5 text-navy" />
                </div>
                <p className="text-[16px] text-ink/80 leading-relaxed m-0">
                  School life also brings families together through activities such as Family Sports Day, where parents, children and extended family members can share experiences, enjoy friendly competition and create lasting memories together.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* ── Divider ── */}
        <div className="w-full h-px bg-line/80 my-16 lg:my-20" />

        {/* ── Section 2: Alumni Community ── */}
        <div className="flex flex-col lg:flex-row-reverse items-center gap-10 lg:gap-16 mb-20 lg:mb-28">
          <div className="w-full lg:w-5/12">
            <div className="relative">
              <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-navy/10 rounded-2xl -z-10" />
              <img
                src={campusImg}
                alt="Alumni Community at GMIS"
                className="relative z-10 w-full h-[360px] md:h-[420px] rounded-2xl shadow-xl object-cover border-4 border-white"
              />
              <div className="absolute -top-4 -left-4 w-20 h-20 bg-gold/15 rounded-2xl -z-10" />
            </div>
          </div>

          <div className="w-full lg:w-7/12 flex flex-col gap-6 text-left">
            <div>
              <div className="inline-flex items-center gap-2 mb-3 px-3.5 py-1 rounded-full border border-gold/30 bg-gold/10">
                <span className="text-gold text-[11px] font-bold tracking-[0.2em] uppercase">Lifelong Connection</span>
              </div>
              <h2 className="m-0 font-heading font-bold text-3xl md:text-4xl text-navy tracking-tight">
                Alumni Community
              </h2>
              <div className="w-16 h-1 bg-gold mt-4" />
            </div>

            <div className="p-6 rounded-2xl bg-white border border-line shadow-sm">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-gold/15 text-gold-ink flex items-center justify-center shrink-0 mt-0.5">
                  <GraduationCap className="w-5 h-5 text-gold-ink" />
                </div>
                <p className="text-[16px] text-ink/80 leading-relaxed m-0">
                  The connection with GMIS continues beyond graduation. Alumni gatherings provide opportunities for former students to reconnect with friends, teachers and the school, while remaining part of the wider GMIS community.
                </p>
              </div>
            </div>

            <div className="p-6 rounded-2xl bg-white border border-line shadow-sm">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-navy/10 text-navy flex items-center justify-center shrink-0 mt-0.5">
                  <HeartHandshake className="w-5 h-5 text-navy" />
                </div>
                <p className="text-[16px] text-ink/80 leading-relaxed m-0">
                  Returning to school also offers alumni a chance to revisit the experiences, relationships and memories that shaped their time at GMIS. Through continued connection with the school community, alumni remain part of the GMIS story even as they move forward into new stages of their lives.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* ── Section 3: Concluding Motto Banner ── */}
        <div className="w-full bg-navy rounded-3xl p-10 md:p-14 text-center text-white shadow-xl relative overflow-hidden">
          <div className="w-16 h-1 bg-gold mx-auto mb-6" />
          <h3 className="font-heading font-bold text-2xl sm:text-3xl md:text-4xl leading-snug max-w-3xl mx-auto tracking-tight">
            “Connecting families. Celebrating shared experiences. Keeping the GMIS community together.”
          </h3>
          <div className="mt-8 flex justify-center">
            <button
              onClick={() => {
                if (setPage) setPage('contact');
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              className="px-8 py-3.5 rounded-lg bg-gold text-navy font-heading font-bold text-xs uppercase tracking-widest hover:bg-gold/90 transition-all shadow-md"
            >
              Contact Us
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}
